import React, { useEffect, useRef, useState } from "react";
import { toast, ToastContainer } from "react-toastify";

import { getPartenaireKataById } from "../../services/partenairesKataApi";

import Footer from "../Home/Footer";

import AuthIcons from "../../hooks/AuthIcons";
import useScrollToTop from "../../hooks/useScrollToTop";
import { useUser } from "../../contexts/UserContext";

import KataFilters from "./components/KataFilters";
import KataHero from "./components/KataHero";
import KataPrivacyNotice from "./components/KataPrivacyNotice";
import KataResults from "./components/KataResults";
import KataStats from "./components/KataStats";
import KataToolbar from "./components/KataToolbar";
import ManageAnnonceModal from "./components/ManageAnnonceModal";
import PartenaireDetailsModal from "./components/PartenaireDetailsModal";
import PartenaireFormModal from "./components/PartenaireFormModal";

import usePartenairesKata from "./hooks/usePartenairesKata";

import {
  getDepartmentByCode,
  normalizeDepartmentCode,
} from "./utils/kataUtils";

const VIEW_MODE_STORAGE_KEY = "monapplikata.results.viewMode";

export default function PartenairesKataPage() {
  useScrollToTop();

  const { user } = useUser();

  const {
    partenaires,
    departmentCounts,
    meta,
    stats,
    filters,
    loading,
    statsLoading,
    error,
    setFilter,
    setLocationFilters,
    resetFilters,
    refresh,
  } = usePartenairesKata();

  const [viewMode, setViewMode] = useState(() => {
    const storedValue = localStorage.getItem(VIEW_MODE_STORAGE_KEY);

    return storedValue === "map" ? "map" : "cards";
  });

  const [createModalOpen, setCreateModalOpen] = useState(false);

  const [detailsPartenaire, setDetailsPartenaire] = useState(null);

  const [manageModalOpen, setManageModalOpen] = useState(false);

  const [managePartenaire, setManagePartenaire] = useState(null);

  const [editState, setEditState] = useState(null);

  const sharedAnnonceLoadedRef = useRef(false);

  useEffect(() => {
    localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
  }, [viewMode]);

  useEffect(() => {
    if (sharedAnnonceLoadedRef.current) {
      return;
    }

    sharedAnnonceLoadedRef.current = true;

    const searchParams = new URLSearchParams(window.location.search);

    const sharedAnnonceId = searchParams.get("annonce");

    if (!sharedAnnonceId || !/^\d+$/.test(sharedAnnonceId)) {
      return;
    }

    let componentMounted = true;

    const loadSharedAnnonce = async () => {
      try {
        const response = await getPartenaireKataById(sharedAnnonceId);

        if (componentMounted && response?.data) {
          setDetailsPartenaire(response.data);
        }
      } catch (error) {
        if (componentMounted) {
          toast.error(
            error.message ||
              "Cette annonce est introuvable ou n’est plus active.",
          );

          const url = new URL(window.location.href);
          url.searchParams.delete("annonce");

          window.history.replaceState(
            {},
            "",
            `${url.pathname}${url.search}${url.hash}`,
          );
        }
      }
    };

    loadSharedAnnonce();

    return () => {
      componentMounted = false;
    };
  }, []);

  const openPartenaireDetails = (partenaire) => {
    if (!partenaire?.id) {
      return;
    }

    setDetailsPartenaire(partenaire);

    const url = new URL(window.location.href);

    url.searchParams.set("annonce", partenaire.id);

    window.history.replaceState(
      {},
      "",
      `${url.pathname}${url.search}${url.hash}`,
    );
  };

  const closePartenaireDetails = () => {
    setDetailsPartenaire(null);

    const url = new URL(window.location.href);

    url.searchParams.delete("annonce");

    window.history.replaceState(
      {},
      "",
      `${url.pathname}${url.search}${url.hash}`,
    );
  };

  const scrollToResults = () => {
    document.getElementById("annonces-kata")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const handleDepartmentSelect = (code) => {
    const normalizedCode = normalizeDepartmentCode(code);

    const department = getDepartmentByCode(normalizedCode);

    setLocationFilters({
      region: department?.region || "",
      departement: normalizedCode,
    });

    setViewMode("cards");

    window.setTimeout(() => {
      scrollToResults();
    }, 100);
  };

  const handleSaved = () => {
    refresh();
  };

  const handleEditRequested = ({ partenaire, credentials }) => {
    closeManageModal();

    setEditState({
      partenaire,
      credentials,
    });
  };

  const openManageModal = (partenaire = null) => {
    setManagePartenaire(partenaire);
    setManageModalOpen(true);
  };

  const closeManageModal = () => {
    setManageModalOpen(false);
    setManagePartenaire(null);
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-white">
      {!user ? <AuthIcons /> : null}

      <main>
        <KataHero
          stats={stats}
          statsLoading={statsLoading}
          onCreate={() => setCreateModalOpen(true)}
          onDiscover={scrollToResults}
        />

        <KataStats />

        <section id="annonces-kata" className="scroll-mt-24 py-16 sm:py-20">
          <div className="mx-auto max-w-7xl space-y-8 px-4 sm:px-6 lg:px-8">
            <KataFilters
              filters={filters}
              total={meta.total}
              onChange={setFilter}
              onLocationChange={setLocationFilters}
              onReset={resetFilters}
            />

            <KataToolbar
              total={meta.total}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              onCreate={() => setCreateModalOpen(true)}
              onManage={() => openManageModal()}
            />

            <KataResults
              viewMode={viewMode}
              partenaires={partenaires}
              departmentCounts={departmentCounts}
              meta={meta}
              loading={loading}
              error={error}
              onDetails={openPartenaireDetails}
              onManage={setManagePartenaire}
              onDepartmentSelect={handleDepartmentSelect}
              onPageChange={(page) => {
                setFilter("page", page);

                document.getElementById("annonces-kata")?.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                });
              }}
              onReset={resetFilters}
              onManage={setManagePartenaire}
            />

            <KataPrivacyNotice />
          </div>
        </section>
      </main>

      <PartenaireDetailsModal
        partenaire={detailsPartenaire}
        isOpen={Boolean(detailsPartenaire)}
        onClose={closePartenaireDetails}
      />

      <ManageAnnonceModal
        partenaire={managePartenaire}
        isOpen={manageModalOpen}
        onClose={closeManageModal}
        onEdit={handleEditRequested}
        onChanged={refresh}
      />

      <PartenaireFormModal
        mode="create"
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSaved={handleSaved}
      />

      <PartenaireFormModal
        mode="edit"
        partenaire={editState?.partenaire}
        credentials={editState?.credentials}
        isOpen={Boolean(editState)}
        onClose={() => setEditState(null)}
        onSaved={handleSaved}
      />

      <ToastContainer
        position="bottom-right"
        autoClose={3500}
        newestOnTop
        theme="colored"
      />

      <Footer />
    </div>
  );
}
